import type { AdemeDpe } from "../hooks/use-dpe-lookup";
// @ts-expect-error pure js
import { FileTag } from "./constants/documents";

/** Champs du formulaire du bien qu'un DPE de l'ADEME peut renseigner. */
export interface DpePrefillValues {
  indice_dpe?: string;
  date_dpe?: string;
  surface_total?: number | string;
  year_built?: string;
  /** « individuel » ou « collectif ». */
  chauffage?: string;
  eau_chaude?: string;
  /** « individuel » (maison) ou « collectif » (appartement). */
  type_habitat?: string;
  not_applicable_diagnostics?: string[];
}

const GAS_ENERGY = /gaz|gpl|butane|propane/i;

/** Année de construction au format du formulaire : année sur 4 chiffres, sinon période de l'ADEME. */
export function yearBuiltFromDpe(dpe: AdemeDpe): string | undefined {
  if (dpe.annee_construction) {
    return String(dpe.annee_construction);
  }
  const period = dpe.periode_construction?.trim() ?? "";
  const avant = /^avant (\d{4})$/i.exec(period);
  if (avant) {
    return `avant ${avant[1]}`;
  }
  const apres = /^apr[èe]s (\d{4})$/i.exec(period);
  if (apres) {
    return `après ${apres[1]}`;
  }
  const entre = /^(\d{4})\s*-\s*(\d{4})$/.exec(period);
  if (entre) {
    return `entre ${entre[1]} et ${entre[2]}`;
  }
  return undefined;
}

function installationKind(value?: string): string | undefined {
  const lower = value?.toLowerCase() ?? "";
  if (lower.startsWith("individuel")) {
    return "individuel";
  }
  if (lower.startsWith("collectif")) {
    return "collectif";
  }
  return undefined;
}

/**
 * Le logement est-il alimenté au gaz d'après les énergies du DPE ? `undefined`
 * quand le DPE ne renseigne pas à la fois le chauffage et l'eau chaude.
 */
export function gasPresence(dpe: AdemeDpe): boolean | undefined {
  const energies = [
    dpe.type_energie_principale_chauffage,
    dpe.type_energie_principale_ecs,
    dpe.type_energie_n1,
    dpe.type_energie_n2,
    dpe.type_energie_n3,
  ].filter((energy): energy is string => Boolean(energy));
  if (energies.some((energy) => GAS_ENERGY.test(energy))) {
    return true;
  }
  if (
    dpe.type_energie_principale_chauffage &&
    dpe.type_energie_principale_ecs
  ) {
    return false;
  }
  return undefined;
}

/**
 * Valeurs à reporter dans le formulaire depuis un DPE choisi. La classe, la
 * date du DPE, le chauffage, l'eau chaude et le type d'habitat viennent du
 * diagnostiqueur et remplacent la saisie ; la surface et l'année de
 * construction ne sont reprises que si elles sont vides. Sans gaz, le
 * diagnostic gaz est dispensé ; avec gaz, une dispense existante est levée.
 */
export function dpePrefillValues(
  dpe: AdemeDpe,
  current: DpePrefillValues,
): DpePrefillValues {
  const next: DpePrefillValues = { indice_dpe: dpe.etiquette_dpe };
  if (dpe.date_etablissement_dpe) {
    next.date_dpe = dpe.date_etablissement_dpe;
  }
  if (!current.surface_total && dpe.surface_habitable_logement) {
    next.surface_total = dpe.surface_habitable_logement;
  }
  if (!current.year_built) {
    const yearBuilt = yearBuiltFromDpe(dpe);
    if (yearBuilt) {
      next.year_built = yearBuilt;
    }
  }
  const chauffage = installationKind(dpe.type_installation_chauffage);
  if (chauffage) {
    next.chauffage = chauffage;
  }
  const eauChaude = installationKind(dpe.type_installation_ecs);
  if (eauChaude) {
    next.eau_chaude = eauChaude;
  }
  const batiment = dpe.type_batiment?.toLowerCase();
  if (batiment === "maison") {
    next.type_habitat = "individuel";
  } else if (batiment === "appartement" || batiment === "immeuble") {
    next.type_habitat = "collectif";
  }
  const gas = gasPresence(dpe);
  if (gas !== undefined) {
    const tags = current.not_applicable_diagnostics ?? [];
    next.not_applicable_diagnostics = gas
      ? tags.filter((tag) => tag !== FileTag.GAZ)
      : tags.includes(FileTag.GAZ)
      ? tags
      : [...tags, FileTag.GAZ];
  }
  return next;
}

/** Résumé lisible des valeurs reportées, pour confirmer le préremplissage. */
export function describePrefill(
  values: DpePrefillValues,
  gas: boolean | undefined,
): string[] {
  const items: string[] = [];
  if (values.indice_dpe) {
    items.push(`classe ${values.indice_dpe}`);
  }
  if (values.surface_total) {
    items.push(`surface ${values.surface_total} m²`);
  }
  if (values.year_built) {
    items.push(`construction ${values.year_built}`);
  }
  if (values.chauffage) {
    items.push(`chauffage ${values.chauffage}`);
  }
  if (values.eau_chaude) {
    items.push(
      `eau chaude ${
        values.eau_chaude === "collectif" ? "collective" : "individuelle"
      }`,
    );
  }
  if (values.type_habitat) {
    items.push(
      values.type_habitat === "individuel"
        ? "logement indépendant"
        : "logement dans un immeuble",
    );
  }
  if (gas === true) {
    items.push("gaz présent");
  } else if (gas === false) {
    items.push("sans gaz, diagnostic gaz non demandé");
  }
  return items;
}
